import { useState } from 'react';
import { dateForDay } from '../engine/seed';
import type { DailyLedger } from '../state/statsLogic';
import { buildCalendar, maxCalendarPage } from '../utils/calendar';
import { IconButton } from './Button';

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const WEEKS = 5;

function level(won: number, total: number): number {
  if (won <= 0) return 0;
  if (total <= 0) return 1;
  const f = won / total;
  return f >= 0.75 ? 4 : f >= 0.5 ? 3 : f >= 0.25 ? 2 : 1;
}

/**
 * A heat-map of daily wins. Each past day is a button: tap it to review that
 * day's results or play the dailies you missed (the archive).
 */
export function DailyCalendar({
  dailies,
  modeCount,
  onPickDay,
}: {
  dailies: DailyLedger;
  modeCount: number;
  onPickDay?: (dayNum: number) => void;
}) {
  const [page, setPage] = useState(0);
  const maxPage = maxCalendarPage(WEEKS);
  const grid = buildCalendar(dailies, WEEKS, new Date(), page);
  const first = grid[0][0];
  const last = grid[grid.length - 1][6];
  const fmt = (n: number) => dateForDay(n).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  return (
    <div className="cv-calendar">
      <div className="cv-calendar__nav">
        <IconButton icon="back" label="Earlier weeks" size={18} disabled={page >= maxPage} onClick={() => setPage((p) => p + 1)} />
        <span className="cv-faint cv-mono">
          {fmt(first.dayNum)} – {fmt(last.dayNum)}
        </span>
        <IconButton
          icon="back"
          label="Later weeks"
          size={18}
          className="cv-calendar__next"
          disabled={page === 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        />
      </div>
      <div className="cv-calendar__weekdays" aria-hidden="true">
        {WEEKDAYS.map((d, i) => (
          <span key={i}>{d}</span>
        ))}
      </div>
      <div role="grid" aria-label="Daily calendar — pick a day to review or play it">
        {grid.map((week, wi) => (
          <div className="cv-calendar__week" key={wi} role="row">
            {week.map((day) => {
              const disabled = day.future || day.beforeStart || !onPickDay;
              const label = `${fmt(day.dayNum)}, daily #${day.dayNum}: ${
                day.future ? 'not out yet' : day.beforeStart ? 'before ClueVine' : `${day.won} of ${modeCount} solved`
              }${day.isToday ? ' (today)' : ''}`;
              return (
                <span key={day.key} role="gridcell">
                  <button
                    type="button"
                    className="cv-calendar__day"
                    data-level={level(day.won, modeCount)}
                    data-done={day.won > 0 ? 'true' : undefined}
                    data-today={day.isToday ? 'true' : undefined}
                    data-future={day.future || day.beforeStart ? 'true' : undefined}
                    disabled={disabled}
                    aria-label={label}
                    title={label}
                    onClick={() => onPickDay?.(day.dayNum)}
                  />
                </span>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
